import { Channel, Presences, type Models } from 'appwrite';
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router';
import { appwriteClient } from '../../services/appwrite/client';
import { runCommand } from '../../services/appwrite/command-gateway';
import { subscribeToRows, type RealtimeMessage } from '../../services/appwrite/realtime';
import { reportClientError } from '../../services/appwrite/report-client-error';
import { useSession } from '../session/SessionContext';
import { useQueryClient } from '@tanstack/react-query';

const HEARTBEAT_MS = 30_000;
const TAB_KEY = 'rs-presence-id';

interface PresenceValue {
  isOnline(userId: string): boolean;
}

const PresenceContext = createContext<PresenceValue | null>(null);
const presences = new Presences(appwriteClient);

function tabPresenceId(userId: string): string {
  const key = `${TAB_KEY}:${userId}`;
  const existing = sessionStorage.getItem(key);
  if (existing) return existing;
  const created = crypto.randomUUID();
  sessionStorage.setItem(key, created);
  return created;
}

/** Ubicación funcional, sin identificadores de perfiles ni parámetros privados. */
export function presenceLocation(pathname: string): Record<string, string> {
  const chat = pathname.match(/^\/chat\/([A-Z0-9]{8})\/?$/i);
  if (chat?.[1]) return { location: 'chat', chatId: chat[1].toUpperCase() };
  if (pathname === '/chat' || pathname.startsWith('/chat/')) return { location: 'tavern' };
  if (pathname === '/notifications') return { location: 'notifications' };
  if (pathname.startsWith('/users/')) return { location: 'profile' };
  if (pathname.startsWith('/admin')) return { location: 'panel' };
  if (pathname.startsWith('/system')) return { location: 'panel' };
  if (pathname.startsWith('/panel')) return { location: 'panel' };
  if (pathname.startsWith('/playgrounds')) return { location: 'playgrounds' };
  if (pathname.startsWith('/activities')) return { location: 'activities' };
  return { location: 'site' };
}

function isDelete(event: RealtimeMessage<Models.Presence>): boolean {
  return event.events.some((name) => name.endsWith('.delete'));
}

/**
 * Presencia de cuenta compartida por todo el sitio.
 *
 * Cada pestaña conserva una fila breve. El servidor deduplica por `userId`, y
 * el chat mira además `metadata.chatId`: estar en el sitio no equivale a estar
 * sentado en una mesa.
 */
export function PresenceProvider({ children }: PropsWithChildren) {
  const { state } = useSession();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [onlineUserIds, setOnlineUserIds] = useState<Set<string>>(new Set());
  const userId = state.status === 'authenticated' ? state.profile.user.id : '';
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;

  useEffect(() => {
    if (!userId) {
      setOnlineUserIds(new Set());
      return undefined;
    }
    // Las páginas se prueban sin conectarse a producción. La unidad de esta
    // pieza prueba por separado cómo se deriva la ubicación.
    if (import.meta.env.MODE === 'test') {
      setOnlineUserIds(new Set([userId]));
      return undefined;
    }

    let active = true;
    const presenceId = tabPresenceId(userId);
    const refresh = async () => {
      try {
        await runCommand('touch_user_presence', {
          presenceId,
          ...presenceLocation(pathnameRef.current),
        });
        if (!active) return;
        setOnlineUserIds((current) => new Set(current).add(userId));
        void queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      } catch (caught) {
        console.error('[Rol Sensorial] No se pudo renovar la presencia.', caught);
        reportClientError({
          source: 'web-app',
          operation: 'presence-heartbeat',
          level: 'warning',
          message: 'No se pudo renovar la presencia.',
          details: String(caught),
        });
      }
    };

    void presences.list({ total: false, ttl: 0 }).then((result) => {
      if (active) setOnlineUserIds(new Set(result.presences.map((presence) => presence.userId)));
    }).catch((caught: unknown) => {
      console.error('[Rol Sensorial] No se pudo leer la presencia global.', caught);
      reportClientError({
        source: 'web-app',
        operation: 'presence-list',
        level: 'warning',
        message: 'No se pudo leer la presencia global.',
        details: String(caught),
      });
    });
    void refresh();
    const timer = window.setInterval(() => void refresh(), HEARTBEAT_MS);
    const refreshOnActivity = () => void refresh();
    // Los navegadores reducen los temporizadores cuando quedan detrás de otra
    // aplicación. Renovar también en estos cambios da margen antes y después
    // de esa pausa sin confundirla con abandonar el sitio.
    document.addEventListener('visibilitychange', refreshOnActivity);
    window.addEventListener('focus', refreshOnActivity);
    window.addEventListener('online', refreshOnActivity);
    window.addEventListener('pageshow', refreshOnActivity);
    const unsubscribe = subscribeToRows<Models.Presence>([Channel.presences()], (event) => {
      const payload = event.payload;
      if (!payload?.userId) return;
      setOnlineUserIds((current) => {
        const next = new Set(current);
        if (isDelete(event)) next.delete(payload.userId);
        else next.add(payload.userId);
        return next;
      });
      void queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
    });

    return () => {
      active = false;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', refreshOnActivity);
      window.removeEventListener('focus', refreshOnActivity);
      window.removeEventListener('online', refreshOnActivity);
      window.removeEventListener('pageshow', refreshOnActivity);
      unsubscribe();
      // No se elimina aquí: React puede desmontar y montar de nuevo el árbol
      // durante una actualización sin que la persona haya abandonado el
      // sitio. El TTL del servidor retira la fila si de verdad cesan los
      // latidos; salir de una mesa sigue siendo una acción explícita aparte.
    };
  }, [queryClient, userId]);

  // Cambiar de lugar se publica en el momento, sin esperar al próximo latido.
  // La fila no se elimina en este efecto: otra pestaña o una actualización
  // anterior no puede borrar por carrera la ubicación recién escrita.
  useEffect(() => {
    if (!userId || import.meta.env.MODE === 'test') return;
    void runCommand('touch_user_presence', {
      presenceId: tabPresenceId(userId),
      ...presenceLocation(location.pathname),
    }).catch((caught: unknown) => {
      console.error('[Rol Sensorial] No se pudo actualizar la ubicación de presencia.', caught);
      reportClientError({
        source: 'web-app',
        operation: 'presence-location',
        level: 'warning',
        message: 'No se pudo actualizar la ubicación de presencia.',
        details: String(caught),
      });
    });
  }, [location.pathname, userId]);

  const isOnline = useCallback((candidate: string) => onlineUserIds.has(candidate), [onlineUserIds]);
  const value = useMemo(() => ({ isOnline }), [isOnline]);
  return <PresenceContext.Provider value={value}>{children}</PresenceContext.Provider>;
}

export function usePresence(): PresenceValue {
  const value = useContext(PresenceContext);
  if (!value) throw new Error('usePresence debe utilizarse dentro de PresenceProvider.');
  return value;
}
